import * as React from "react";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { InputText } from "@bizform/primereact/components/inputtext/InputText";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { ComponentHelper, Debug, InputNumeric, InputNumericProps } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = InputNumericProps & {
};

type WrappedCompState = InputNumericProps & {
    value?: number | string;
    tabindex?: number;
    thousandSeparator?: string;
    precision?: number;
    allowNegative?: boolean;
    range?: any;
    prefix?: string;
    tooltipAttribute?: TooltipOptions & {text?: string};
    onChange?( originalEvent: Event, maskValue: string, value: number): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Hwansoon Park, modified Heungsoo Ham
 * @class BfInputTextNumeric
 * @description BfInputTextNumeric component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<number>>()
export class BfInputTextNumeric extends AbstractComponentElement<Props, State> {
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_ZA4ETuM8l0sfRuwO98vFBzwHbE9oGdw",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.inputtextnumeric.label,
        description: I18n.componentElement.inputtextnumeric.description,
        icon: "",
        dataType: [DataType.INTEGER, DataType.NUMBER],
        property: null
    };

    focus() {
        if (this._inputNumeric && this._inputNumeric.input) {
            this._inputNumeric.input.focus();
        } else if (this._inputNumeric) {
            this._inputNumeric.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(property, this);
            if (!this.accessibility) {
                if (newState.wrappedCompState.range) {
                    newState.wrappedCompState = {
                        ...newState.wrappedCompState,
                        ...newState.wrappedCompState.range,
                    };
                    if (this.state.wrappedCompState) {
                        if (this.state.wrappedCompState.value || this.state.wrappedCompState.value === 0) {
                            newState.wrappedCompState.value = this.state.wrappedCompState.value;
                        } else {
                            delete(newState.wrappedCompState.value);
                        }
                    }
                }
            }
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: number | string): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: number | string): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: value
            }
        };

        this.setState(newState);
    }

    @ReactComponentElementInit(BfInputTextNumeric)
    static init(): ComponentElement<number> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    protected _inputNumeric: any;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {},
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    componentDidUpdate() {
        if (this._inputNumeric && this.state.wrappedCompState.tabindex !== this.___tabindex) {
            ComponentHelper.tabindex(this.displayMode, this._inputNumeric, this.state.wrappedCompState.tabindex);
            this.___tabindex = this.state.wrappedCompState.tabindex;
        }
    }

    componentWillUnmount(): void {
        this._inputNumeric = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    protected _onChangeEvent(event): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: this.accessibility ? event.target.value : event.value
            }
        };
        this.setState(newState, this._callbackSetState);
    }

    protected _callbackSetState() {
        this.emit(this.state.wrappedCompState.value);
        if (this.state.wrappedFormState && this.state.wrappedFormState.onChange) {
            this.emitFormState(this.state.wrappedFormState.onChange);
        }
    }

    render(): React.ReactNode {
        if (!this.state.show) {
            return null;
        } else {
            return this._render();
        }
    }

    protected _render(): React.ReactNode {
        if (this.accessibility && this.displayMode !== ComponentElementDisplayMode.PRINT) {
            const newProps = {
                value: this.state.wrappedCompState.value,
                tooltipAttribute: this.state.wrappedCompState.tooltipAttribute,
                onChange: this.state.wrappedCompState.onChange
            } as any;
            const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
            delete(newProps.tooltipAttribute);
            return <InputText
                {...newProps}
                style={this.state.wrappedCompState.style}
                className={this.state.wrappedCompState.className}
                id={this.elementId}
                key={key}
                ref={this._setRef}
                disabled={this.displayMode === ComponentElementDisplayMode.DISABLED}
                readOnly={this.displayMode === ComponentElementDisplayMode.READONLY}
            />;
        } else {
            return <InputNumeric
                {...this.state.wrappedCompState}
                id={this.elementId}
                ref={this._setRef}
                disabled={this.displayMode === ComponentElementDisplayMode.DISABLED}
                readOnly={this.displayMode === ComponentElementDisplayMode.READONLY}
                isPrint={this.displayMode === ComponentElementDisplayMode.PRINT}
            />;
        }
    }

    protected _setRef(ref) {
        if (ref) {
            this._inputNumeric = ref.input || ref.element;
            this.readyComponent();
        }
    }
}